import { Prompt } from '../types';
import { INTERPRETATION_SYSTEM_PROMPT, INTERPRETATION_USER_TEMPLATE } from './interpretationPrompt';

export const subtestNames: Record<string, string> = {
  block_design: 'Klocki',
  similarities: 'Podobieństwa',
  matrix_reasoning: 'Rozumowanie matrycowe',
  digit_span: 'Powtarzanie liczb',
  coding: 'Kodowanie',
  vocabulary: 'Słownictwo',
  figure_weights: 'Wagi figur',
  visual_puzzles: 'Puzzle wizualne',
  picture_span: 'Rozpiętość obrazkowa',
  symbol_search: 'Szukanie symboli'
};

export const compositeNames: Record<string, string> = {
  vci: 'Wskaźnik Rozumienia Słownego (VCI)',
  vsi: 'Wskaźnik Przestrzenny (VSI)',
  fri: 'Wskaźnik Rozumowania Płynnego (FRI)',
  wmi: 'Wskaźnik Pamięci Roboczej (WMI)',
  psi: 'Wskaźnik Szybkości Przetwarzania (PSI)',
  fsiq: 'Pełnoskalowy IQ (FSIQ)'
};

/**
 * Domeny WISC-V — jedno źródło prawdy: CSS custom properties w src/index.css
 * (:root = hexy kliniczne light, .dark = warianty rozjaśnione wg audytu WCAG).
 * Wartości to stringi `var(--domain-*)` — działają w color / fill / stroke /
 * backgroundColor itd., ale NIE wolno ich konkatenować z alfą hex
 * (np. `${color}40` daje niepoprawny CSS). Do wariantów przezroczystych
 * użyj domainColorAlpha().
 */
export const domainColors: Record<string, string> = {
  vci: 'var(--domain-vci)',   // Blue
  vsi: 'var(--domain-vsi)',   // Green
  fri: 'var(--domain-fri)',   // Purple
  wmi: 'var(--domain-wmi)',   // Orange
  psi: 'var(--domain-psi)',   // Red
  fsiq: 'var(--domain-fsiq)'  // Gray
};

/**
 * Warianty „strong" — wyłącznie do TEKSTU w kolorze domeny na jasnych tłach
 * (light = -700, WCAG ≥4.5:1 na białym; dark = te same wartości co domainColors).
 * Paski, wykresy i kropki zostają na domainColors (tła mają wystarczający kontrast).
 */
export const domainColorsStrong: Record<string, string> = {
  vci: 'var(--domain-vci-strong)',
  vsi: 'var(--domain-vsi-strong)',
  fri: 'var(--domain-fri-strong)',
  wmi: 'var(--domain-wmi-strong)',
  psi: 'var(--domain-psi-strong)',
  fsiq: 'var(--domain-fsiq-strong)'
};

/**
 * Przezroczysty wariant koloru domenowego — zamiennik dawnej konkatenacji
 * hex+alpha (`${hex}18`, `${hex}50`). color-mix() działa zarówno z var(--domain-*),
 * jak i z literalnym hexem fallbacku.
 * @param color  kolor bazowy (np. domainColors.vci albo '#EC4899')
 * @param percent krycie w procentach (0–100), np. 9 ≈ dawne hex '18', 31 ≈ '50'
 */
export const domainColorAlpha = (color: string, percent: number): string =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

export const subtestDomains: Record<string, string> = {
  similarities: 'vci',
  vocabulary: 'vci',
  block_design: 'vsi',
  visual_puzzles: 'vsi',
  matrix_reasoning: 'fri',
  figure_weights: 'fri',
  digit_span: 'wmi',
  picture_span: 'wmi',
  coding: 'psi',
  symbol_search: 'psi'
};

export const defaultPrompts: Prompt[] = [
  {
    // Prompt v2.1 (ewaluacja 2026-09-30): instrukcja systemowa z twardymi zasadami +
    // wiadomość z faktami policzonymi przez kod. Treść: ./interpretationPrompt.ts
    // (jedno źródło prawdy, wspólne z /new). id bez zmian — zachowuje wybór użytkownika.
    id: 'weronika',
    name: 'Weronika v2.1 - Fakty z danych (zalecany)',
    description: 'Styl formalny Weroniki; model redaguje wyłącznie fakty policzone przez program (bez konfabulacji obserwacji, z uwagą o brakujących podtestach)',
    isCustom: false,
    systemPrompt: INTERPRETATION_SYSTEM_PROMPT,
    prompt: INTERPRETATION_USER_TEMPLATE
  },
  {
    id: 'anna',
    name: 'Anna - Styl zwięzły',
    description: 'Krótkie, precyzyjne opisy z konkretnymi zaleceniami',
    isCustom: false,
    prompt: `Jako doświadczony psycholog, napisz zwięzłą interpretację wyników WISC-V.

Dane: {PATIENT_NAME}, VCI={VCI_SCORE}, VSI={VSI_SCORE}, FRI={FRI_SCORE}, WMI={WMI_SCORE}, PSI={PSI_SCORE}, FSIQ={FSIQ_SCORE}

Styl: Zwięzły, konkretny, profesjonalny. Krótkie akapity, jasne sformułowania.

Odpowiedz w formacie Markdown z tymi nagłówkami:

## Ogólna ocena
(2 zdania o poziomie funkcjonowania)

## Mocne strony
(2-3 zdania o mocnych stronach)

## Obszary wymagające wsparcia
(2-3 zdania o trudnościach)

## Funkcjonowanie szkolne
(2 zdania o funkcjonowaniu szkolnym)

## Zalecenia dla nauczycieli
(Lista 5-7 konkretnych strategii z myślnikami)

## Zalecenia dla rodziców
(Lista 4-6 praktycznych wskazówek z myślnikami)

NIE używaj formatu JSON - pisz naturalny tekst Markdown.`
  },
  {
    id: 'maria',
    name: 'Maria - Styl narracyjny',
    description: 'Opowieściowy styl z przykładami i metaforami',
    isCustom: false,
    prompt: `Jako psycholog z 20-letnim doświadczeniem, napisz interpretację w stylu narracyjnym.

Dane dziecka: {PATIENT_NAME}
Wyniki: VCI={VCI_SCORE}, VSI={VSI_SCORE}, FRI={FRI_SCORE}, WMI={WMI_SCORE}, PSI={PSI_SCORE}, FSIQ={FSIQ_SCORE}

Styl: Ciepły, opisowy, z przykładami sytuacji życiowych. Używaj metafor i analogii.

Odpowiedz w formacie Markdown z tymi nagłówkami:

## Ogólna ocena
(Opisowy akapit o dziecku)

## Mocne strony
(Ciepły opis mocnych stron z przykładami)

## Obszary wymagające wsparcia
(Empatyczny opis trudności)

## Funkcjonowanie szkolne
(Opowieściowy opis sytuacji szkolnych)

## Zalecenia dla nauczycieli
(Lista z myślnikami - praktyczne strategie)

## Zalecenia dla rodziców
(Lista z myślnikami - wsparcie domowe)

NIE używaj formatu JSON - pisz naturalny tekst Markdown.`
  }
];
